import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Pencil, Trash2, ChevronUp, ChevronDown, Plus, Zap, ListOrdered } from 'lucide-react';
import { toast } from 'sonner';

const uid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2));

/**
 * Shared editor for the per-proposal T&C list.
 * Works for cinema / newspaper / messaging proposals.
 *
 * Props:
 *   value:    [{ id, order, text, source, preset_id?, included? }]
 *   onChange: (next) => void
 *   disabled: boolean
 *   title:    optional header text
 */
export default function ProposalConditionsEditor({ value, onChange, disabled, title }) {
  const [presets, setPresets] = useState([]);
  const [editing, setEditing] = useState(null); // {id, text}
  const [addOpen, setAddOpen] = useState(false);
  const [newText, setNewText] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/condition-presets');
        const rows = r?.data;
        setPresets(Array.isArray(rows) ? rows : []);
      } catch {
        setPresets([]);
      }
    })();
  }, []);

  const items = useMemo(
    () => (value || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
    [value],
  );

  const commit = (next) => {
    const normalised = next.map((c, i) => ({ ...c, order: i + 1 }));
    onChange(normalised);
  };

  const addClause = (text, from) => {
    const t = (text || '').trim();
    if (!t) return;
    commit([
      ...items,
      {
        id: uid(),
        order: items.length + 1,
        text: t,
        source: from?.source || 'custom',
        preset_id: from?.preset_id,
        included: true,
      },
    ]);
  };

  const removeClause = (id) => commit(items.filter((c) => c.id !== id));
  const toggleIncluded = (id) =>
    commit(items.map((c) => (c.id === id ? { ...c, included: !c.included } : c)));
  const move = (id, dir) => {
    const idx = items.findIndex((c) => c.id === id);
    if (idx < 0) return;
    const to = idx + dir;
    if (to < 0 || to >= items.length) return;
    const next = items.slice();
    [next[idx], next[to]] = [next[to], next[idx]];
    commit(next);
  };

  const saveEdit = () => {
    if (!editing) return;
    const txt = (editing.text || '').trim();
    if (!txt) {
      toast.error('Clause text cannot be empty');
      return;
    }
    commit(items.map((c) => (c.id === editing.id ? { ...c, text: txt } : c)));
    setEditing(null);
  };

  return (
    <Card className="p-4" data-testid="conditions-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-800" data-testid="conditions-editor-title">
            {title || 'Terms and conditions for this proposal'}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Changes apply only to this proposal. The master Terms stay unchanged.
          </div>
        </div>
        {!disabled && (
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="quick-add-conditions-btn">
                  <Zap className="w-4 h-4 mr-1.5" /> Quick add
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Reusable conditions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {presets.length === 0 && (
                  <div className="px-2 py-3 text-xs text-slate-500">
                    No presets yet. Owner/Admin can add presets in Proposal setup → Conditions library.
                  </div>
                )}
                {presets.map((p) => (
                  <DropdownMenuItem
                    key={p.id}
                    onClick={() => addClause(p.body, { source: 'preset', preset_id: p.id })}
                    data-testid={`quick-add-preset-${p.id}`}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">{p.label}</span>
                      <span className="text-xs text-slate-500 line-clamp-2">{p.body}</span>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setNewText('');
                setAddOpen(true);
              }}
              data-testid="add-custom-clause-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add clause
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {items.length === 0 && (
          <div className="text-sm text-slate-500 py-4 text-center border-dashed border rounded-md">
            No conditions on this proposal yet.
          </div>
        )}
        {items.map((c, idx) => (
          <div
            key={c.id}
            className={`flex items-start gap-2 rounded-md border p-2 ${
              c.included === false ? 'bg-slate-50 opacity-70' : 'bg-white'
            }`}
            data-testid={`condition-row-${c.id}`}
          >
            <div className="pt-1 text-xs text-slate-400 font-mono w-6 text-center shrink-0">
              <ListOrdered className="w-3.5 h-3.5 mx-auto mb-1 text-slate-300" />
              {idx + 1}
            </div>
            <div className="flex-1 text-sm text-slate-700 whitespace-pre-wrap pt-1">{c.text}</div>
            {!disabled && (
              <div className="flex items-center gap-1 shrink-0">
                <div className="flex items-center gap-1 mr-2">
                  <Switch
                    checked={c.included !== false}
                    onCheckedChange={() => toggleIncluded(c.id)}
                    aria-label="Include this clause"
                    data-testid={`condition-include-switch-${c.id}`}
                  />
                  <span className="text-[11px] text-slate-500">
                    {c.included === false ? 'Excluded' : 'Included'}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => move(c.id, -1)}
                  disabled={idx === 0}
                  aria-label="Move up"
                  data-testid={`condition-up-${c.id}`}
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => move(c.id, 1)}
                  disabled={idx === items.length - 1}
                  aria-label="Move down"
                  data-testid={`condition-down-${c.id}`}
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setEditing({ id: c.id, text: c.text })}
                  aria-label="Edit clause"
                  data-testid={`condition-edit-${c.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-rose-600 hover:text-rose-700"
                  onClick={() => removeClause(c.id)}
                  aria-label="Remove clause"
                  data-testid={`condition-remove-${c.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="condition-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit clause</DialogTitle>
            <DialogDescription>
              The master Terms are not changed. Only this proposal is affected.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={6}
            value={editing?.text || ''}
            onChange={(e) => setEditing((p) => ({ ...p, text: e.target.value }))}
            autoFocus
            data-testid="condition-edit-textarea"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="condition-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-condition-dialog">
          <DialogHeader>
            <DialogTitle>Add a clause</DialogTitle>
            <DialogDescription>
              Will be added at the end of the list. Reorder from the arrows after adding.
            </DialogDescription>
          </DialogHeader>
          <Label>Clause text</Label>
          <Textarea
            rows={6}
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            autoFocus
            placeholder="e.g. 100% advance payment is required to lock the campaign."
            data-testid="add-condition-textarea"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                addClause(newText);
                setAddOpen(false);
              }}
              data-testid="add-condition-save"
            >
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
